import { useEffect, useId, useRef, type ReactNode } from "react"
import cx from "clsx"
import { DashMark, TickMark } from "./CheckboxMarks"
import { describedBy } from "./describedBy"
import { FieldHint } from "./FieldHint"
import styles from "./Checkbox.module.css"

type CheckboxProps = {
  checked: boolean
  label: ReactNode
  onChange?: (checked: boolean) => void
  size?: "small" | "large"
  disabled?: boolean
  indeterminate?: boolean
  required?: boolean
  /** Accessible name; falls back to the visible label when omitted. */
  ariaLabel?: string
  /** How to answer it, shown under the label and read with the control. */
  hint?: string
  /** What is wrong with it, shown under the hint and read with the control. */
  error?: string
  className?: string
}

// Design-system checkbox (see docs/design — "Checkbox Large/Small"). A real
// <input> drives state and accessibility; the adjacent <span> is the styled box
// (tick / dash drawn as inline SVG). Supports unchecked, checked, indeterminate
// and disabled states in two sizes.
export function Checkbox({
  checked,
  label,
  onChange,
  size = "large",
  disabled = false,
  indeterminate = false,
  required = false,
  ariaLabel,
  hint,
  error,
  className,
}: CheckboxProps) {
  const hintId = useId()
  const errorId = useId()
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate
  }, [indeterminate])

  const control = (
    <label
      className={cx(styles.root, styles[size], className)}
      data-disabled={disabled || undefined}
    >
      <input
        ref={inputRef}
        type="checkbox"
        className={styles.input}
        checked={checked}
        disabled={disabled}
        required={required}
        aria-label={ariaLabel}
        aria-describedby={describedBy(hint && hintId, error && errorId)}
        aria-invalid={!!error || undefined}
        onChange={(e) => onChange?.(e.target.checked)}
      />
      <span className={styles.box} aria-hidden="true">
        {indeterminate ? (
          <DashMark className={styles.mark} />
        ) : (
          <TickMark className={styles.mark} />
        )}
      </span>
      {label != null && (
        <span className={styles.label}>
          {label}
          {required && (
            <span aria-hidden="true" className={styles.required}>
              {" "}
              *
            </span>
          )}
        </span>
      )}
    </label>
  )
  if (!hint && !error) return control
  return (
    <span className={cx(styles.field, { [styles.error]: error })}>
      {control}
      {hint && <FieldHint id={hintId}>{hint}</FieldHint>}
      {error && (
        <span id={errorId} className={styles.errorMessage} aria-live="polite">
          {error}
        </span>
      )}
    </span>
  )
}
